import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { setSidebarCollapsed } from '@/store/ui/uiSlice';
import { selectSidebarCollapsed } from '@/store/ui/uiSelectors';
import { selectUserProfile } from '@/store/user/userSelectors';
import { logout } from '@/store/auth/authSlice';
import NavbarProgressBar from '@/components/layout/NavbarProgressBar';
import { getPageTitle, getInitials } from '@/helper/utilities';
import styles from './Header.module.css';

const Header = ({ onMobileMenuToggle, mobileOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userProfile = useSelector(selectUserProfile);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleSidebar = () => {
    if (window.innerWidth < 768) {
      if (onMobileMenuToggle) onMobileMenuToggle();
    } else {
      dispatch(setSidebarCollapsed(!collapsed));
    }
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    dispatch(logout()).finally(() => navigate('/login'));
  };

  const handleSettings = () => {
    setUserMenuOpen(false);
    navigate('/app/settings');
  };

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <button
          className={styles.menuButton}
          onClick={handleToggleSidebar}
          aria-label={mobileOpen ? 'Close navigation' : 'Toggle navigation'}
        >
          {mobileOpen
            ? <XMarkIcon className={styles.menuIcon} />
            : <Bars3Icon className={styles.menuIcon} />}
        </button>
        <h1 className={styles.pageTitle}>{pageTitle}</h1>
      </div>

      <div className={styles.right}>
        <NavbarProgressBar />

        <div className={styles.userMenu} ref={userMenuRef}>
          <button
            className={styles.userMenuTrigger}
            onClick={() => setUserMenuOpen((open) => !open)}
            aria-expanded={userMenuOpen}
            aria-haspopup="menu"
          >
            <div className={styles.avatar} aria-hidden="true">
              {getInitials(userProfile?.name)}
            </div>
            <span className={styles.userName}>{userProfile?.name || 'User'}</span>
          </button>

          {userMenuOpen && (
            <div className={styles.dropdown} role="menu">
              <div className={styles.dropdownHeader}>
                <span className={styles.dropdownName}>{userProfile?.name || 'User'}</span>
                <span className={styles.dropdownEmail}>{userProfile?.email || ''}</span>
              </div>
              <div className={styles.dropdownDivider} />
              <button
                className={styles.dropdownItem}
                onClick={handleSettings}
                role="menuitem"
              >
                Account Settings
              </button>
              <div className={styles.dropdownDivider} />
              <button
                className={classNames(styles.dropdownItem, styles.dropdownItemDanger)}
                onClick={handleLogout}
                role="menuitem"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;